import type { ExecutionStepProto } from "build-ts/chat_session";
import { ArtifactType, artifactTypeFromJSON, ExecutionStatusType } from "build-ts/common";
import { DarwinToolType } from "build-ts/darwin_agent";
import { findToolType, type ToolExecutionRun } from "./executionStepToolRunGroups";

function kgDataframeName(step: ExecutionStepProto): string | undefined {
	for (const a of step.artifacts ?? []) {
		if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.KG_DATAFRAME) continue;
		const n = a.kg_dataframe?.header?.name?.trim();
		if (n) return n;
	}
	return undefined;
}

function firstKgDataframeNameInRun(steps: ExecutionStepProto[]): string | undefined {
	for (const s of steps) {
		const n = kgDataframeName(s);
		if (n) return n;
	}
	return undefined;
}

function chartName(step: ExecutionStepProto): string | undefined {
	for (const a of step.artifacts ?? []) {
		if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) continue;
		const charts = (
			a.dataframe_analysis as unknown as { charts?: Array<{ name?: string }> } | undefined
		)?.charts ?? [];
		for (const c of charts) {
			const n = c.name?.trim();
			if (n) return n;
		}
	}
	return undefined;
}

function chartSummaryInRun(steps: ExecutionStepProto[]): { firstName?: string; count: number } {
	let firstName: string | undefined;
	const names = new Set<string>();
	for (let i = steps.length - 1; i >= 0; i--) {
		const n = chartName(steps[i]!);
		if (!firstName && n) firstName = n;
	}
	for (const step of steps) {
		for (const a of step.artifacts ?? []) {
			if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.DATAFRAME_ANALYSIS) continue;
			const charts = (
				a.dataframe_analysis as unknown as { charts?: Array<{ name?: string }> } | undefined
			)?.charts ?? [];
			for (const c of charts) {
				const n = c.name?.trim();
				if (n) names.add(n);
			}
		}
	}
	return { firstName, count: names.size };
}

function scenarioJobName(step: ExecutionStepProto): string | undefined {
	for (const a of step.artifacts ?? []) {
		if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.SCENARIO_JOB) continue;
		const n = a.scenario_job?.header?.name?.trim();
		if (n) return n;
	}
	return undefined;
}

function firstScenarioJobNameInRun(steps: ExecutionStepProto[]): string | undefined {
	for (let i = steps.length - 1; i >= 0; i--) {
		const n = scenarioJobName(steps[i]!);
		if (n) return n;
	}
	return undefined;
}

/**
 * User-facing line while this tool run is still in flight (label updates as `status` advances).
 */
export function labelForInProgressToolRun(
	toolType: DarwinToolType | undefined,
	status: ExecutionStatusType | undefined,
	stepsSoFar: ExecutionStepProto[],
): string {
	const table = firstKgDataframeNameInRun(stepsSoFar);
	const { firstName: chart } = chartSummaryInRun(stepsSoFar);
	const scenario = firstScenarioJobNameInRun(stepsSoFar);
	const tableSuffix = table ? ` · ${table}` : "";
	const chartSuffix = chart ? ` · ${chart}` : "";

	// Status-led labels (apply across tools that emit these phases)
	switch (status) {
		case ExecutionStatusType.FETCHING_ANALYSIS_OUTPUT:
			return "Searching past runs";
		case ExecutionStatusType.BUILDING_ANALYSIS_OUTPUT:
			return "Building analysis output";
		case ExecutionStatusType.BUILDING_CHART:
			return `Building chart${chartSuffix || tableSuffix}`;
		case ExecutionStatusType.FETCHING_PAST_JOB_RUNS:
			return "Fetching past workflow runs";
		case ExecutionStatusType.UPDATING_SCENARIO_METADATA:
			return scenario ? `Updating scenario · ${scenario}` : "Updating scenario metadata";
		case ExecutionStatusType.SAVING_SCENARIO:
			return scenario ? `Saving scenario · ${scenario}` : "Saving scenario";
		default:
			break;
	}

	switch (toolType) {
		case DarwinToolType.ANALYZE_DATAFRAMES_TOOL:
			switch (status) {
				case ExecutionStatusType.EXECUTING_TOOL_CALL:
				case ExecutionStatusType.VALIDATING_TOOL_CALL:
					return "Preparing analysis";
				default:
					return "Analyzing dataframes";
			}
		case DarwinToolType.DATAFRAME_TOOL:
			switch (status) {
				case ExecutionStatusType.EXECUTING_TOOL_CALL:
				case ExecutionStatusType.BUILDING_DATAFRAME:
					return "Building dataframe";
				case ExecutionStatusType.VALIDATING_TOOL_CALL:
				case ExecutionStatusType.VALIDATING_DATAFRAME:
					return "Validating dataframe";
				case ExecutionStatusType.EXECUTING_DATAFRAME:
					return `Querying data table${tableSuffix}`;
				default:
					return `Working on data${tableSuffix}`;
			}
		case DarwinToolType.GET_SCENARIO_DETAIL_TOOL:
			switch (status) {
				case ExecutionStatusType.EXECUTING_TOOL_CALL:
				case ExecutionStatusType.VALIDATING_TOOL_CALL:
					return "Preparing to read analysis";
				case ExecutionStatusType.FETCHING_SCENARIO_DETAIL:
					return scenario ? `Reading analysis · ${scenario}` : "Reading analysis";
				default:
					return scenario ? `Reading analysis · ${scenario}` : "Reading analysis";
			}
		case DarwinToolType.SKILL_TOOL:
			switch (status) {
				case ExecutionStatusType.EXECUTING_TOOL_CALL:
				case ExecutionStatusType.VALIDATING_TOOL_CALL:
					return "Starting skill";
				case ExecutionStatusType.LOADING_SCENARIO_MANAGEMENT_SKILL:
					return "Loading scenario management skill";
				case ExecutionStatusType.LOADING_SCENARIO_SYNTHESIS_SKILL:
					return "Loading scenario synthesis skill";
				default:
					return "Running skill";
			}
		case DarwinToolType.GET_PAST_JOB_RUNS:
			switch (status) {
				case ExecutionStatusType.EXECUTING_TOOL_CALL:
				case ExecutionStatusType.VALIDATING_TOOL_CALL:
					return "Fetching past job runs";
				default:
					return "Searching past runs";
			}
		case DarwinToolType.UPDATE_SCENARIO_METADATA_TOOL:
			return "Updating scenario metadata";
		case DarwinToolType.SAVE_SCENARIO_TOOL:
			return "Saving scenario";
		case DarwinToolType.KG_OBSERVATION_DATA:
			switch (status) {
				case ExecutionStatusType.BUILDING_KG_OBSERVATION:
					return "Building observation";
				case ExecutionStatusType.EXECUTING_KG_OBSERVATION:
					return "Running observation";
				default:
					return "Fetching observations";
			}
		case DarwinToolType.AGENT_TOOL:
			return "Running sub-agent";
		case DarwinToolType.LOAD_SKILL_TOOL:
			switch (status) {
				case ExecutionStatusType.LOADING_SKILL:
					return "Loading skill";
				default:
					return "Loading skill";
			}
		default:
			return "Unrecognized tool";
	}
}

/**
 * Final line when the tool run has finished successfully.
 */
function hasAnalysisOutputArtifact(steps: ExecutionStepProto[]): boolean {
	for (const s of steps) {
		for (const a of s.artifacts ?? []) {
			if (artifactTypeFromJSON(a.artifact_type) === ArtifactType.ANALYSIS_OUTPUT) return true;
		}
	}
	return false;
}

export type ToolRunLabelParts = {
	full: string;
	/** Text before the type icon (completed runs with a named artifact). */
	prefix: string;
	/** Artifact / analysis name shown after the icon; null → no inline icon, use `full` as one line. */
	artifactHighlight: string | null;
};

function partsCompletedSuccess(toolType: DarwinToolType | undefined, steps: ExecutionStepProto[]): ToolRunLabelParts {
	const table = firstKgDataframeNameInRun(steps);
	const { firstName: chart, count: chartCount } = chartSummaryInRun(steps);
	const scenario = firstScenarioJobNameInRun(steps);

	if (hasAnalysisOutputArtifact(steps)) {
		const full = "Searched past runs";
		return { full, prefix: full, artifactHighlight: null };
	}

	switch (toolType) {
		case DarwinToolType.ANALYZE_DATAFRAMES_TOOL:
			if (chart) {
				if (chartCount > 1) {
					return {
						full: `Built ${chartCount} charts`,
						prefix: `Built ${chartCount} charts`,
						artifactHighlight: null,
					};
				}
				return {
					full: `Built chart · ${chart}`,
					prefix: "Built chart · ",
					artifactHighlight: chart,
				};
			}
			return { full: "Analyzed dataframes", prefix: "Analyzed dataframes", artifactHighlight: null };
		case DarwinToolType.DATAFRAME_TOOL:
			if (table) return { full: `Analyzed ${table}`, prefix: "Analyzed ", artifactHighlight: table };
			return { full: "Analyzed data", prefix: "Analyzed data", artifactHighlight: null };
		case DarwinToolType.GET_SCENARIO_DETAIL_TOOL:
			if (scenario) return { full: `Read analysis · ${scenario}`, prefix: "Read analysis · ", artifactHighlight: scenario };
			return { full: "Read analysis", prefix: "Read analysis", artifactHighlight: null };
		case DarwinToolType.SKILL_TOOL:
			return { full: "Loaded skill", prefix: "Loaded skill", artifactHighlight: null };
		case DarwinToolType.GET_PAST_JOB_RUNS:
			return { full: "Searched past runs", prefix: "Searched past runs", artifactHighlight: null };
		case DarwinToolType.UPDATE_SCENARIO_METADATA_TOOL:
			if (scenario) return { full: `Updated scenario · ${scenario}`, prefix: "Updated scenario · ", artifactHighlight: scenario };
			return { full: "Updated scenario", prefix: "Updated scenario", artifactHighlight: null };
		case DarwinToolType.SAVE_SCENARIO_TOOL:
			if (scenario) return { full: `Saved scenario · ${scenario}`, prefix: "Saved scenario · ", artifactHighlight: scenario };
			return { full: "Saved scenario", prefix: "Saved scenario", artifactHighlight: null };
		case DarwinToolType.KG_OBSERVATION_DATA:
			if (table) return { full: `Explored ${table}`, prefix: "Explored ", artifactHighlight: table };
			return { full: "Explored observation", prefix: "Explored observation", artifactHighlight: null };
		case DarwinToolType.AGENT_TOOL:
			return { full: "Sub-agent finished", prefix: "Sub-agent finished", artifactHighlight: null };
		case DarwinToolType.LOAD_SKILL_TOOL:
			return { full: "Skill loaded", prefix: "Skill loaded", artifactHighlight: null };
		default:
			return { full: "Tool finished", prefix: "Tool finished", artifactHighlight: null };
	}
}

export function labelForCompletedToolRunSuccess(toolType: DarwinToolType | undefined, steps: ExecutionStepProto[]): string {
	const p = partsCompletedSuccess(toolType, steps);
	return p.full;
}

export function labelForCompletedToolRunFailure(toolType: DarwinToolType | undefined): string {
	switch (toolType) {
		case DarwinToolType.ANALYZE_DATAFRAMES_TOOL:
			return "Analysis failed";
		case DarwinToolType.DATAFRAME_TOOL:
		case DarwinToolType.KG_OBSERVATION_DATA:
			return "Query failed";
		case DarwinToolType.GET_SCENARIO_DETAIL_TOOL:
			return "Could not read analysis";
		case DarwinToolType.SKILL_TOOL:
			return "Skill failed";
		case DarwinToolType.GET_PAST_JOB_RUNS:
			return "Failed to fetch past runs";
		case DarwinToolType.UPDATE_SCENARIO_METADATA_TOOL:
			return "Failed to update analysis";
		case DarwinToolType.SAVE_SCENARIO_TOOL:
			return "Failed to save analysis";
		case DarwinToolType.LOAD_SKILL_TOOL:
			return "Failed to load skill";
		case DarwinToolType.AGENT_TOOL:
			return "Failed to run sub-agent";
		case DarwinToolType.BUILD_ANALYSIS_OUTPUT_TOOL:
			return "Failed to build analysis output";
		default:
			return "Tool failed";
	}
}

/** Prefix / artifact split for UI (icon sits between them when `artifactHighlight` is set). */
export function toolRunLabelParts(run: ToolExecutionRun): ToolRunLabelParts {
	const tool = findToolType(run.steps);
	const latest = run.steps[run.steps.length - 1]!;

	if (run.failed) {
		const full = labelForCompletedToolRunFailure(tool);
		return { full, prefix: full, artifactHighlight: null };
	}
	if (run.completed) {
		return partsCompletedSuccess(tool, run.steps);
	}
	const full = labelForInProgressToolRun(tool, latest.status, run.steps);
	return { full, prefix: full, artifactHighlight: null };
}